import arRuntimeTranslations from '@/locales/runtime/ar';
import enRuntimeTranslations from '@/locales/runtime/en';
import frRuntimeTranslations from '@/locales/runtime/fr';

export type RuntimeLang = 'en' | 'fr' | 'ar';

type DynamicRule = {
  pattern: RegExp;
  replacement: string;
};

const RUNTIME_TRANSLATIONS: Record<RuntimeLang, Record<string, string>> = {
  en: enRuntimeTranslations,
  fr: frRuntimeTranslations,
  ar: arRuntimeTranslations,
};

// Primary translations belong in src/locales/<lang>.ts.
// This runtime layer is only a safety net for still-hardcoded UI phrases.

const escapeRegExp = (input: string) => input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const isWordChar = (ch: string) => /[A-Za-z0-9\u0600-\u06FF]/.test(ch);

const buildPhraseRegex = (phrase: string) => {
  const escaped = escapeRegExp(phrase);
  const first = phrase[0] || '';
  const last = phrase[phrase.length - 1] || '';
  const needsLeftBoundary = isWordChar(first);
  const needsRightBoundary = isWordChar(last);

  if (needsLeftBoundary && needsRightBoundary) {
    return new RegExp(
      `(^|[^A-Za-z0-9\\u0600-\\u06FF])(${escaped})(?=$|[^A-Za-z0-9\\u0600-\\u06FF])`,
      'g'
    );
  }

  if (needsLeftBoundary) {
    return new RegExp(`(^|[^A-Za-z0-9\\u0600-\\u06FF])(${escaped})`, 'g');
  }

  if (needsRightBoundary) {
    return new RegExp(`(${escaped})(?=$|[^A-Za-z0-9\\u0600-\\u06FF])`, 'g');
  }

  return new RegExp(`(${escaped})`, 'g');
};

const replaceWithBoundaryAwareness = (
  text: string,
  regex: RegExp,
  replacement: string,
  originalPhrase: string
) => {
  return text.replace(regex, (...args) => {
    const match = args[0] as string;
    const group1 = args[1] as string | undefined;
    const group2 = args[2] as string | undefined;

    if (group2 !== undefined) {
      return `${group1}${replacement}`;
    }

    if (group1 === originalPhrase) {
      return replacement;
    }

    return match;
  });
};

const shouldSkipLocalization = (node: Node | null) => {
  if (!node || typeof document === 'undefined') return false;
  const element =
    node.nodeType === Node.ELEMENT_NODE
      ? (node as HTMLElement)
      : (node.parentElement as HTMLElement | null);

  return Boolean(element?.closest('[data-no-runtime-localize="true"]'));
};

const DYNAMIC_TO_ENGLISH: DynamicRule[] = [
  { pattern: /\bIl faut encore\s+(\d+)\s+joueurs dans la file\b/g, replacement: 'Need $1 more players in queue' },
  { pattern: /\bAffichage\s+(\d+)\s*-\s*(\d+)\s+sur\s+(\d+)\b/g, replacement: 'Showing $1 - $2 of $3' },
  { pattern: /\bAffichage\s+(\d+)\s*\/\s*(\d+)\b/g, replacement: 'Showing $1 / $2' },
  { pattern: /\bPage\s+(\d+)\s+sur\s+(\d+)\b/g, replacement: 'Page $1 of $2' },
  { pattern: /\bPlaces membres restantes\s*:\s*(\d+)\b/g, replacement: 'Remaining member slots: $1' },
  { pattern: /\bSelectionne\s*:\s*(\d+)\b/g, replacement: 'Selected: $1' },
  { pattern: /\bLignes CSV\s*:\s*(\d+)\b/g, replacement: 'CSV rows: $1' },
  { pattern: /\bImporter la selection\s*\((\d+)\)\b/g, replacement: 'Import Selected ($1)' },
  { pattern: /\bVoir tous les matchs termines\s*\((\d+)\s+(?:montré|affiches)\)\b/g, replacement: 'View all completed matches ($1 shown)' },
  { pattern: /\bVoir vos matchs termines\s*\((\d+)\s+(?:montré|affiches)\)\b/g, replacement: 'View your completed matches ($1 shown)' },
  { pattern: /\bMatchs actifs\s*\((\d+)\)\b/g, replacement: 'Active Matches ($1)' },
  { pattern: /(.+)\s+a gagne\s+(\d+)%\s+\((\d+)\/(\d+)\)\s+du temps contre\s+(.+?)(?:\.)?$/g, replacement: '$1 won $2% ($3/$4) of the time against $5' },
  {
    pattern: /Allow\s+suffle\s+of\s+the\s+queue\s*\(Please\s+Note\s+it\s+will\s+reset\s+wait\s+temps\s+dans\s+la\s+file\s+and\s+shuffle\s+players\s+position\s+in\s+queue\)/g,
    replacement: 'Allow suffle of the queue (Please Note it will reset wait time in queue and shuffle players position in queue)',
  },
  { pattern: /هناك حاجة إلى\s*(\d+)\s*لاعبين إضافيين في قائمة الانتظار/g, replacement: 'Need $1 more players in queue' },
  { pattern: /عرض\s+(\d+)\s*-\s*(\d+)\s+من\s+(\d+)/g, replacement: 'Showing $1 - $2 of $3' },
  { pattern: /عرض\s+(\d+)\s*\/\s*(\d+)/g, replacement: 'Showing $1 / $2' },
  { pattern: /الصفحة\s+(\d+)\s+من\s+(\d+)/g, replacement: 'Page $1 of $2' },
  { pattern: /المقاعد المتبقية للأعضاء:\s*(\d+)/g, replacement: 'Remaining member slots: $1' },
  { pattern: /المحدد:\s*(\d+)/g, replacement: 'Selected: $1' },
  { pattern: /صفوف CSV:\s*(\d+)/g, replacement: 'CSV rows: $1' },
  { pattern: /استيراد المحدد\s*\((\d+)\)/g, replacement: 'Import Selected ($1)' },
  { pattern: /عرض جميع المباريات المكتملة\s*\((\d+)\s+معروضة\)/g, replacement: 'View all completed matches ($1 shown)' },
  { pattern: /عرض مبارياتك المكتملة\s*\((\d+)\s+معروضة\)/g, replacement: 'View your completed matches ($1 shown)' },
  { pattern: /المباريات النشطة\s*\((\d+)\)/g, replacement: 'Active Matches ($1)' },
  { pattern: /(\d+)\s+رؤى هذا الأسبوع/g, replacement: '$1 insights in this week' },
  {
    pattern: /Allow\s+suffle\s+of\s+the\s+queue\s*\(Please[\s\S]*?reset\s+wait[\s\S]*?queue[\s\S]*?shuffle\s+players\s+position\s+in\s+queue\)/g,
    replacement: 'Allow suffle of the queue (Please Note it will reset wait time in queue and shuffle players position in queue)',
  },
];

const DYNAMIC_FROM_ENGLISH: Record<Exclude<RuntimeLang, 'en'>, DynamicRule[]> = {
  fr: [
    {
      pattern: /We sent a reset link to\s+(.+?)\.\s+The link expires shortly, so use it soon\./g,
      replacement:
        'Nous avons envoye un lien de reinitialisation a $1. Le lien expire bientot, utilisez-le rapidement.',
    },
    {
      pattern: /These email\(s\) are already exist\./g,
      replacement: 'Ces e-mails existent deja.',
    },
    {
      pattern: /(Remove|Retirer|إزالة) those from selection, then continue import\./g,
      replacement: 'Retirez-les de la selection, puis poursuivez l import.',
    },
    {
      pattern: /(.+)\.\s+These email\(s\) are already exist\. Remove those from selection, then continue import\./g,
      replacement: '$1. Ces e-mails existent deja. Retirez-les de la selection, puis poursuivez l import.',
    },
    {
      pattern: /(.+)\.\s+These email\(s\) are already exist\. Retirer those from selection, then continue import\./g,
      replacement: '$1. Ces e-mails existent deja. Retirez-les de la selection, puis poursuivez l import.',
    },
    {
      pattern: /(.+)\.\s*These email\(s\) are already exist\.Retirer those from selection, then continue import\./g,
      replacement: '$1. Ces e-mails existent deja. Retirez-les de la selection, puis poursuivez l import.',
    },
    {
      pattern: /(.+)\.\s*These email\(s\) are already exist\.إزالة those from selection, then continue import\./g,
      replacement: '$1. Ces e-mails existent deja. Retirez-les de la selection, puis poursuivez l import.',
    },
    {
      pattern: /Are you sure you want to remove\s+(.+)\?/g,
      replacement: 'Voulez-vous vraiment retirer $1 ?',
    },
    { pattern: /Generate\s+weekly\s+insight/g, replacement: 'Generer une insight hebdomadaire' },
    { pattern: /Already\s+generated\s+this\s+week/g, replacement: 'Deja genere cette semaine' },
    {
      pattern: /Enable\s+2FA\s+for\s+login\s+\(email\s+OTP\s+will\s+be\s+required\s+every\s+time\)\./g,
      replacement: "Activer la 2FA pour la connexion (un OTP par e-mail sera requis a chaque fois).",
    },
    { pattern: /(.+)\s+added to queue/g, replacement: '$1 ajoute a la file' },
    { pattern: /(.+)\s+removed from queue/g, replacement: '$1 retire de la file' },
    { pattern: /Imported\s+(\d+)\s+coaches\s+successfully\./g, replacement: '$1 coachs importes avec succes.' },
    { pattern: /Imported\s+(\d+)\s+players\s+successfully\./g, replacement: '$1 joueurs importes avec succes.' },
    {
      pattern: /You can only select\s+(\d+)\s+members based on remaining plan slots\./g,
      replacement: 'Vous pouvez seulement selectionner $1 membres selon les places restantes du forfait.',
    },
    {
      pattern: /You can select only\s+(\d+)\s+members for this package\./g,
      replacement: 'Vous pouvez selectionner seulement $1 membres pour ce forfait.',
    },
    {
      pattern: /You can select at most\s+(\d+)\s+members\./g,
      replacement: 'Vous pouvez selectionner au maximum $1 membres.',
    },
    { pattern: /Member activated/g, replacement: 'Membre active' },
    { pattern: /Member deactivated/g, replacement: 'Membre desactive' },
    {
      pattern: /Account created, but billing checkout failed:\s*(.+)/g,
      replacement: "Le compte a ete cree, mais le paiement a echoue : $1",
    },
    {
      pattern: /Active member limit reached for\s+(PACKAGE\d)\s+\((\d+)\s+active members\)\.\s+Upgrade package to add more members\./g,
      replacement: 'Limite de membres actifs atteinte pour $1 ($2 membres actifs). Mettez a niveau le forfait pour ajouter plus de membres.',
    },
    { pattern: /\bNeed\s+(\d+)\s+more players in queue\b/g, replacement: 'Il faut encore $1 joueurs dans la file' },
    { pattern: /\bShowing\s+(\d+)\s*-\s*(\d+)\s+of\s+(\d+)\b/g, replacement: 'Affichage $1 - $2 sur $3' },
    { pattern: /\bShowing\s+(\d+)\s*\/\s*(\d+)\b/g, replacement: 'Affichage $1 / $2' },
    { pattern: /\bPage\s+(\d+)\s+of\s+(\d+)\b/g, replacement: 'Page $1 sur $2' },
    { pattern: /\bRemaining member slots:\s*(\d+)\b/g, replacement: 'Places membres restantes : $1' },
    { pattern: /\bSelected:\s*(\d+)\b/g, replacement: 'Selectionne : $1' },
    { pattern: /\bCSV rows:\s*(\d+)\b/g, replacement: 'Lignes CSV : $1' },
    { pattern: /\bImport Selected\s*\((\d+)\)\b/g, replacement: 'Importer la selection ($1)' },
    { pattern: /\bView all completed matches\s*\((\d+)\s+shown\)\b/g, replacement: 'Voir tous les matchs termines ($1 affiches)' },
    { pattern: /\bView your completed matches\s*\((\d+)\s+shown\)\b/g, replacement: 'Voir vos matchs termines ($1 affiches)' },
    { pattern: /\bActive Matches\s*\((\d+)\)\b/g, replacement: 'Matchs actifs ($1)' },
    { pattern: /\b(\d+)\s+insights\s+in\s+this\s+week\b/g, replacement: '$1 insights cette semaine' },
    {
      pattern: /Allow\s+suffle\s+of\s+the\s+queue\s*\(Please[\s\S]*?reset\s+wait[\s\S]*?queue[\s\S]*?shuffle\s+players\s+position\s+in\s+queue\)/g,
      replacement:
        "Autoriser le melange de la file (veuillez noter que cela reinitialisera le temps d'attente dans la file et melangera la position des joueurs dans la file)",
    },
    { pattern: /(.+)\s+won\s+(\d+)%\s+\((\d+)\/(\d+)\)\s+of the time against\s+(.+?)(?:\.)?$/g, replacement: '$1 a gagne $2% ($3/$4) du temps contre $5.' },
  ],
  ar: [
    {
      pattern: /We sent a reset link to\s+(.+?)\.\s+The link expires shortly, so use it soon\./g,
      replacement:
        'لقد ارسلنا رابطا لاعادة التعيين إلى $1. سينتهي الرابط قريبا، لذا استخدمه بسرعة.',
    },
    {
      pattern: /These email\(s\) are already exist\./g,
      replacement: 'هذه الرسائل الإلكترونية موجودة بالفعل.',
    },
    {
      pattern: /(Remove|Retirer|إزالة) those from selection, then continue import\./g,
      replacement: 'أزلها من التحديد ثم تابع الاستيراد.',
    },
    {
      pattern: /(.+)\.\s+These email\(s\) are already exist\. Remove those from selection, then continue import\./g,
      replacement: '$1. هذه الرسائل الإلكترونية موجودة بالفعل. أزلها من التحديد ثم تابع الاستيراد.',
    },
    {
      pattern: /(.+)\.\s+These email\(s\) are already exist\. Retirer those from selection, then continue import\./g,
      replacement: '$1. هذه الرسائل الإلكترونية موجودة بالفعل. أزلها من التحديد ثم تابع الاستيراد.',
    },
    {
      pattern: /(.+)\.\s*These email\(s\) are already exist\.Retirer those from selection, then continue import\./g,
      replacement: '$1. هذه الرسائل الإلكترونية موجودة بالفعل. أزلها من التحديد ثم تابع الاستيراد.',
    },
    {
      pattern: /(.+)\.\s*These email\(s\) are already exist\.إزالة those from selection, then continue import\./g,
      replacement: '$1. هذه الرسائل الإلكترونية موجودة بالفعل. أزلها من التحديد ثم تابع الاستيراد.',
    },
    {
      pattern: /Are you sure you want to remove\s+(.+)\?/g,
      replacement: 'هل أنت متأكد أنك تريد إزالة $1؟',
    },
    { pattern: /(.+)\s+added to queue/g, replacement: 'تمت إضافة $1 إلى قائمة الانتظار' },
    { pattern: /(.+)\s+removed from queue/g, replacement: 'تمت إزالة $1 من قائمة الانتظار' },
    { pattern: /Imported\s+(\d+)\s+coaches\s+successfully\./g, replacement: 'تم استيراد $1 مدربين بنجاح.' },
    { pattern: /Imported\s+(\d+)\s+players\s+successfully\./g, replacement: 'تم استيراد $1 لاعبين بنجاح.' },
    {
      pattern: /You can only select\s+(\d+)\s+members based on remaining plan slots\./g,
      replacement: 'يمكنك تحديد $1 أعضاء فقط بناءً على المقاعد المتبقية في الباقة.',
    },
    {
      pattern: /You can select only\s+(\d+)\s+members for this package\./g,
      replacement: 'يمكنك تحديد $1 أعضاء فقط لهذه الباقة.',
    },
    {
      pattern: /You can select at most\s+(\d+)\s+members\./g,
      replacement: 'يمكنك تحديد $1 أعضاء كحد أقصى.',
    },
    { pattern: /Member activated/g, replacement: 'تم تفعيل العضو' },
    { pattern: /Member deactivated/g, replacement: 'تم إلغاء تفعيل العضو' },
    {
      pattern: /Account created, but billing checkout failed:\s*(.+)/g,
      replacement: 'تم إنشاء الحساب، لكن فشل الدفع: $1',
    },
    {
      pattern: /Active member limit reached for\s+(PACKAGE\d)\s+\((\d+)\s+active members\)\.\s+Upgrade package to add more members\./g,
      replacement: 'تم الوصول إلى حد الأعضاء النشطين لـ $1 ($2 أعضاء نشطين). قم بترقية الباقة لإضافة المزيد من الأعضاء.',
    },
    { pattern: /\bNeed\s+(\d+)\s+more players in queue\b/g, replacement: 'هناك حاجة إلى $1 لاعبين إضافيين في قائمة الانتظار' },
    { pattern: /\bShowing\s+(\d+)\s*-\s*(\d+)\s+of\s+(\d+)\b/g, replacement: 'عرض $1 - $2 من $3' },
    { pattern: /\bShowing\s+(\d+)\s*\/\s*(\d+)\b/g, replacement: 'عرض $1 / $2' },
    { pattern: /\bPage\s+(\d+)\s+of\s+(\d+)\b/g, replacement: 'الصفحة $1 من $2' },
    { pattern: /\bRemaining member slots:\s*(\d+)\b/g, replacement: 'المقاعد المتبقية للأعضاء: $1' },
    { pattern: /\bSelected:\s*(\d+)\b/g, replacement: 'المحدد: $1' },
    { pattern: /\bCSV rows:\s*(\d+)\b/g, replacement: 'صفوف CSV: $1' },
    { pattern: /\bImport Selected\s*\((\d+)\)\b/g, replacement: 'استيراد المحدد ($1)' },
    { pattern: /\bView all completed matches\s*\((\d+)\s+shown\)\b/g, replacement: 'عرض جميع المباريات المكتملة ($1 معروضة)' },
    { pattern: /\bView your completed matches\s*\((\d+)\s+shown\)\b/g, replacement: 'عرض مبارياتك المكتملة ($1 معروضة)' },
    { pattern: /\bActive Matches\s*\((\d+)\)\b/g, replacement: 'المباريات النشطة ($1)' },
    { pattern: /\b(\d+)\s+insights\s+in\s+this\s+week\b/g, replacement: '$1 رؤى هذا الأسبوع' },
    {
      pattern: /Allow\s+suffle\s+of\s+the\s+queue\s*\(Please[\s\S]*?reset\s+wait[\s\S]*?queue[\s\S]*?shuffle\s+players\s+position\s+in\s+queue\)/g,
      replacement: 'السماح بخلط قائمة الانتظار (يرجى الملاحظة أن ذلك سيعيد ضبط وقت الانتظار ويغيّر ترتيب اللاعبين في القائمة)',
    },
    { pattern: /(.+)\s+won\s+(\d+)%\s+\((\d+)\/(\d+)\)\s+of the time against\s+(.+?)(?:\.)?$/g, replacement: '$1 فاز بنسبة $2% ($3/$4) من الوقت ضد $5.' },
  ],
};

const applyRules = (text: string, rules: DynamicRule[]) =>
  rules.reduce((value, rule) => value.replace(rule.pattern, rule.replacement), text);

const toEnglish = (text: string) => {
  let out = applyRules(text, DYNAMIC_TO_ENGLISH);
  for (const language of ['fr', 'ar'] as const) {
    const ordered = Object.entries(RUNTIME_TRANSLATIONS[language]).sort((a, b) => b[1].length - a[1].length);
    for (const [english, localized] of ordered) {
      if (!localized || !out.includes(localized)) continue;
      const regex = buildPhraseRegex(localized);
      out = replaceWithBoundaryAwareness(out, regex, english, localized);
    }
  }
  return out;
};

const fromEnglish = (text: string, language: RuntimeLang) => {
  if (language === 'en') return text;
  let out = applyRules(text, DYNAMIC_FROM_ENGLISH[language]);
  const ordered = Object.entries(RUNTIME_TRANSLATIONS[language]).sort((a, b) => b[0].length - a[0].length);
  for (const [english, localized] of ordered) {
    if (!out.includes(english)) continue;
    const regex = buildPhraseRegex(english);
    out = replaceWithBoundaryAwareness(out, regex, localized, english);
  }
  return out;
};

const replaceText = (text: string, language: RuntimeLang): string => {
  const base = toEnglish(text);
  return fromEnglish(base, language);
};

export const localizeDomOnce = (language: RuntimeLang) => {
  if (typeof document === 'undefined') return;
  const root = document.body;
  if (!root) return;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let node: Node | null = walker.currentNode;

  while (node) {
    if (shouldSkipLocalization(node)) {
      node = walker.nextNode();
      continue;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      const value = node.nodeValue || '';
      const next = replaceText(value, language);
      if (next !== value) node.nodeValue = next;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (!['SCRIPT', 'STYLE', 'NOSCRIPT'].includes(el.tagName)) {
        const attrs = ['placeholder', 'title', 'aria-label'];
        for (const attr of attrs) {
          const value = el.getAttribute(attr);
          if (!value) continue;
          const next = replaceText(value, language);
          if (next !== value) el.setAttribute(attr, next);
        }
      }
    }
    node = walker.nextNode();
  }
};

const localizeNode = (node: Node, language: RuntimeLang) => {
  if (shouldSkipLocalization(node)) return;

  if (node.nodeType === Node.TEXT_NODE) {
    const value = node.nodeValue || '';
    const next = replaceText(value, language);
    if (next !== value) node.nodeValue = next;
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const el = node as HTMLElement;
  if (['SCRIPT', 'STYLE', 'NOSCRIPT'].includes(el.tagName)) return;

  const attrs = ['placeholder', 'title', 'aria-label'];
  for (const attr of attrs) {
    const value = el.getAttribute(attr);
    if (!value) continue;
    const next = replaceText(value, language);
    if (next !== value) el.setAttribute(attr, next);
  }

  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let current: Node | null = walker.currentNode;
  while (current) {
    const value = current.nodeValue || '';
    const next = replaceText(value, language);
    if (next !== value) current.nodeValue = next;
    current = walker.nextNode();
  }
};

export const observeDomLocalization = (language: RuntimeLang) => {
  if (typeof document === 'undefined') return () => undefined;
  const root = document.body;
  if (!root) return () => undefined;

  localizeDomOnce(language);

  let scheduled = false;
  const queue = new Set<Node>();

  const flush = () => {
    scheduled = false;
    const items = Array.from(queue);
    queue.clear();
    items.forEach((node) => localizeNode(node, language));
  };

  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(flush);
  };

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.target) {
        queue.add(mutation.target);
      }
      if (mutation.type === 'characterData' && mutation.target) {
        queue.add(mutation.target);
      }
      mutation.addedNodes.forEach((node) => queue.add(node));
    }
    schedule();
  });

  observer.observe(root, {
    subtree: true,
    childList: true,
    attributes: true,
    characterData: true,
    attributeFilter: ['placeholder', 'title', 'aria-label'],
  });

  return () => observer.disconnect();
};
